import { Routes } from '@angular/router';

import { provideTranslocoScope } from '@jsverse/transloco';
import { provideEffects } from '@ngrx/effects';
import { provideState } from '@ngrx/store';
import { ROUTES } from '@shared/constants/routes/routes.constants';
import { EducationalSectionEffects } from '@store/educational-section/educational-section.effects';
import { EducationalSectionFacade } from '@store/educational-section/educational-section.facade';
import { userFeatureKey, userReducer } from '@store/user/user.reducer';
import { UserCombinationsEffects } from '@store/user-combinations/user-combinations.effects';

import { userRegistrationPrefillResolver } from './components/user-registration/user-registration-prefill.resolver';
import { UserRoleComponent } from './components/user-role/user-role.component';
import { AdminGuard } from './guards/admin.guard';
import { loginGuard } from './guards/login.guard';

export const routes: Routes = [
  {
    path: ROUTES.HOME,
    component: UserRoleComponent,
    pathMatch: 'full',
    canActivate: [loginGuard]
  },
  { path: 'openid/callback', redirectTo: ROUTES.HOME },
  { path: ROUTES.REDIRECT_URI, redirectTo: ROUTES.HOME },
  {
    path: ROUTES.USER_REGISTRATION,
    loadComponent: () =>
      import('./components/user-registration/user-registration.component').then(
        (c) => c.UserRegistrationComponent
      ),
    resolve: { prefill: userRegistrationPrefillResolver },
    canActivate: [loginGuard]
  },
  {
    path: ROUTES.COMBINATIONS,
    providers: [
      provideState(userFeatureKey, userReducer),
      provideEffects([UserCombinationsEffects]),
      provideTranslocoScope('combinations', 'light-protection-references')
    ],
    loadChildren: () => import('./modules/home/home.routing').then((r) => r.homeRoutes),
    canActivate: [loginGuard]
  },
  {
    path: ROUTES.GUIDE,
    loadComponent: () =>
      import('./modules/instructions/instructions.component').then((c) => c.InstructionsComponent),
    providers: [provideTranslocoScope('manual/instructions')],
    canActivate: [loginGuard]
  },
  {
    path: ROUTES.ABBREVIATIONS,
    loadComponent: () =>
      import('./modules/abbreviations/abbreviations.component').then(
        (c) => c.AbbreviationsComponent
      ),
    providers: [provideTranslocoScope('manual/abbreviations')],
    canActivate: [loginGuard]
  },
  {
    path: ROUTES.PORTFOLIO_GUIDE,
    loadChildren: () =>
      import('./modules/portfolio-guide/portfolio-guide.routing').then(
        (r) => r.portfolioGuideRoutes
      ),
    providers: [provideTranslocoScope('portfolio-guide')],
    canActivate: [loginGuard]
  },
  {
    path: ROUTES.EDUCATIONAL_SECTION,
    loadChildren: () =>
      import('./modules/educational-section/educational-section.routing').then(
        (r) => r.educationalSectionRoutes
      ),
    providers: [
      provideTranslocoScope('educational-section'),
      provideEffects([EducationalSectionEffects]),
      EducationalSectionFacade
    ],
    canActivate: [loginGuard]
  },
  {
    path: ROUTES.ADMINISTRATION,
    loadChildren: () => import('./modules/admin/admin.routing').then((r) => r.adminRoutes),
    // loginGuard first: it waits for MSAL to finish the redirect handling, so
    // AdminGuard's authenticated request can actually carry a bearer token.
    canActivate: [loginGuard, AdminGuard]
  },
  {
    path: ROUTES.NOT_FOUND,
    loadComponent: () =>
      import('./modules/error-page/error-page.component').then((c) => c.ErrorPageComponent)
  },
  {
    path: ROUTES.SERVER_ERROR,
    loadComponent: () =>
      import('./modules/error-page/error-page.component').then((c) => c.ErrorPageComponent)
  },
  {
    path: ROUTES.FORBIDDEN,
    loadComponent: () =>
      import('./modules/error-page/error-page.component').then((c) => c.ErrorPageComponent)
  },
  {
    path: ROUTES.LEGAL,
    loadChildren: () => import('./modules/legal/legal.routing').then((r) => r.legalRoutes)
  },
  {
    path: ROUTES.FAQ,
    loadComponent: () => import('./modules/faq/faq.component').then((c) => c.FaqComponent),
    providers: [provideTranslocoScope('manual/faq')],
    canActivate: [loginGuard]
  },
  {
    path: '**',
    redirectTo: ROUTES.NOT_FOUND
  }
];
